import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ClockIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import EventModal from '../components/EventModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { EventData } from '../lib/validation';
import { getTournamentEvents, deleteEvent, isValidUUID, supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface Event extends EventData {
  id: string;
}

interface EventCalendarProps {
  tournamentId: string;
}

const eventTypes = {
  Registration: "bg-blue-100 text-blue-800",
  Tournament: "bg-green-100 text-green-800",
  Ceremony: "bg-amber-100 text-amber-800",
  Social: "bg-orange-100 text-orange-800"
} as const;

function toLocalDateStr(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export default function EventCalendar({ tournamentId }: EventCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [events, setEvents] = useState<Event[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schedulePublished, setSchedulePublished] = useState(true);
  const [updatingSchedule, setUpdatingSchedule] = useState(false);

  const eventDates = useMemo(() => new Set(events.map(e => e.date)), [events]);

  const fetchEvents = async () => {
    if (!tournamentId || !isValidUUID(tournamentId)) {
      setEvents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await getTournamentEvents(tournamentId);
      if (fetchError) throw fetchError;
      const loaded = data || [];
      setEvents(loaded);

      if (loaded.length > 0) {
        const todayStr = toLocalDateStr(new Date());
        const todayHasEvents = loaded.some(e => e.date === todayStr);
        if (!todayHasEvents) {
          const futureDates = loaded.map(e => e.date).filter(d => d >= todayStr).sort();
          const pastDates = loaded.map(e => e.date).filter(d => d < todayStr).sort().reverse();
          const nearest = futureDates[0] || pastDates[0];
          if (nearest) {
            const [y, m, d] = nearest.split('-').map(Number);
            const nearestDate = new Date(y, m - 1, d);
            setCurrentDate(nearestDate);
            setCalendarMonth(new Date(y, m - 1, 1));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduleStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'schedule_published')
        .maybeSingle();

      if (!error && data) {
        setSchedulePublished(data.value === true || data.value === 'true');
      } else {
        setSchedulePublished(true);
      }
    } catch (err) {
      console.error('Error fetching schedule status:', err);
    }
  };

  const toggleScheduleVisibility = async () => {
    try {
      setUpdatingSchedule(true);

      const { data, error: checkError } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'schedule_published')
        .maybeSingle();

      if (checkError && !checkError.message.includes('No rows found')) throw checkError;

      const newStatus = !schedulePublished;

      if (data) {
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({ value: newStatus, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{
            key: 'schedule_published',
            value: newStatus,
            category: 'content',
            description: 'Controls whether the Schedule page is publicly visible',
            is_public: true
          }]);
        if (insertError) throw insertError;
      }

      setSchedulePublished(newStatus);
      toast.success(`Schedule ${newStatus ? 'published' : 'unpublished'} successfully`);
    } catch (err) {
      console.error('Error toggling schedule visibility:', err);
      toast.error('Failed to update schedule visibility');
    } finally {
      setUpdatingSchedule(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchScheduleStatus();
  }, [tournamentId]);

  const handleSelectDay = (date: Date) => {
    setCurrentDate(date);
    setCalendarMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setSelectedView('day');
  };

  const handleAddEvent = () => {
    if (!tournamentId || !isValidUUID(tournamentId)) {
      toast.error('Please select a valid tournament first');
      return;
    }
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleDeleteEvent = (event: Event) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      const { error: deleteError } = await deleteEvent(eventToDelete.id);
      if (deleteError) throw deleteError;
      await fetchEvents();
      toast.success('Event deleted successfully');
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event');
      toast.error('Failed to delete event');
    } finally {
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
    }
  };

  const currentDateEvents = useMemo(() => {
    const dateString = toLocalDateStr(currentDate);
    return events.filter(event => event.date === dateString);
  }, [events, currentDate]);

  const monthCalendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  }, [calendarMonth]);

  const today = new Date();

  if (!tournamentId || !isValidUUID(tournamentId)) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-gray-500">
          Please select a valid tournament to view its events
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-golf-gray">Event Calendar</h2>
        <button
          onClick={handleAddEvent}
          className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Event</span>
        </button>
      </div>

      {/* Schedule Visibility Control */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 sm:px-4 py-2 mb-6">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <div className={`p-1 rounded ${schedulePublished ? 'bg-green-100' : 'bg-yellow-100'}`}>
              {schedulePublished ? (
                <EyeIcon className="w-4 h-4 text-green-600" />
              ) : (
                <EyeSlashIcon className="w-4 h-4 text-yellow-600" />
              )}
            </div>
            <span className="text-xs sm:text-sm font-medium text-golf-gray">
              <span className="hidden sm:inline">Schedule Visibility:</span>
              <span className="sm:hidden">Schedule:</span>
            </span>
            <span className="hidden md:inline text-sm text-gray-600">
              {schedulePublished ? 'Visible to all' : 'Hidden from public'}
            </span>
          </div>
          <button
            onClick={toggleScheduleVisibility}
            disabled={updatingSchedule}
            className={`px-2 sm:px-3 py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
              schedulePublished
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                : 'bg-green-100 text-green-800 hover:bg-green-200'
            } disabled:opacity-50`}
          >
            {updatingSchedule ? (
              <span>Updating...</span>
            ) : schedulePublished ? (
              <>
                <EyeSlashIcon className="w-3.5 h-3.5" />
                <span>Hide</span>
              </>
            ) : (
              <>
                <EyeIcon className="w-3.5 h-3.5" />
                <span>Show</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Compact Mini Calendar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-3">
        {/* Month nav row */}
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-golf-gray">
              {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => { setCurrentDate(new Date()); setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1)); }}
              className="px-2 py-0.5 text-xs font-medium rounded border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Today
            </button>
          </div>
          <button
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 mb-0.5">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{day}</div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7">
          {monthCalendarDays.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} />;
            const dateStr = toLocalDateStr(date);
            const hasEvents = eventDates.has(dateStr);
            const isSelected = isSameDay(date, currentDate);
            const isToday = isSameDay(date, today);
            return (
              <button
                key={i}
                onClick={() => handleSelectDay(date)}
                className={`relative flex flex-col items-center justify-center rounded-lg text-xs font-medium transition-all py-1.5 ${
                  isSelected
                    ? 'bg-golf-orange text-white shadow-sm'
                    : isToday
                    ? 'bg-orange-50 text-golf-orange border border-golf-orange/40'
                    : hasEvents
                    ? 'hover:bg-gray-50 text-gray-800 font-semibold'
                    : 'hover:bg-gray-50 text-gray-400'
                }`}
              >
                {date.getDate()}
                {hasEvents && (
                  <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-golf-orange'}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Events List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            <p>{error}</p>
            <button onClick={fetchEvents} className="mt-4 text-golf-orange hover:text-golf-green">
              Try Again
            </button>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={toLocalDateStr(currentDate)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {currentDateEvents.length > 0 ? (
                currentDateEvents.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow group"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <ClockIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
                          <span className="text-sm font-semibold text-gray-700">{event.time}</span>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                            eventTypes[event.type as keyof typeof eventTypes]
                          }`}>
                            {event.type}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-golf-gray mb-1">{event.title}</h3>
                        <div className="flex items-center gap-1.5 text-gray-500 mb-2 text-sm">
                          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{event.location}</span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-gray-600">{event.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-sm">No events scheduled for this day</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      <EventModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedEvent(null); }}
        event={selectedEvent}
        tournamentId={tournamentId}
        onSuccess={fetchEvents}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={eventToDelete?.title || ''}
      />
    </div>
  );
}
